import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { ViewRequestBaseComponent } from '../../shared/view-request-base/view-request-base.component';
import { RequestBidsSectionComponent } from '../../shared/request-bids-section/request-bids-section.component';
import { CargoRequestService, CargoRequest } from '../../../services/api/cargo-request';
import { CustomsCalculationService, CustomsProduct, UpdateCustomsProductRequest } from '../../../services/api/customs-calculation';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { Subscription, forkJoin } from 'rxjs';
import { ChatThreadsComponent } from '../../shared/chat-threads/chat-threads.component';
import { ConfirmDialogService } from '../../../services/confirm-dialog';
import { DocNumberPipe } from '../../../services/request-number';

@Component({
  selector: 'app-customs-broker-view-request',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, ViewRequestBaseComponent, RequestBidsSectionComponent, ChatThreadsComponent, DocNumberPipe],
  templateUrl: './view-request.html',
  styleUrl: './view-request.scss'
})
export class CustomsBrokerViewRequestComponent implements OnInit, OnDestroy {
  /** Подтверждение на странице вместо системного confirm() — его блокируют встроенные браузеры. */
  private confirmDialog = inject(ConfirmDialogService);

  requestId: number | null = null;
  /** Из query-параметра ?forwarderId= — открыть сразу тред конкретного экспедитора и
   *  проскроллить к чату (переход по клику на push-уведомление о новом сообщении). */
  chatForwarderId: number | null = null;
  request: CargoRequest | null = null;
  loadingRequest = false;
  errorRequest: string | null = null;

  productsForm: FormGroup;
  submitting = false;
  success = false;
  error: string | null = null;

  // Translations
  titleText = 'Просмотр запроса';
  backText = 'Назад к списку';
  chatButtonText = 'Чат';
  loadingText = 'Загрузка...';
  customsProductsSectionTitleText = 'Товары для расчета таможенных платежей';
  productNameText = 'Наименование';
  productTnvedText = 'Код ТНВЭД';
  productCostText = 'Стоимость товара';
  customsFeesText = 'Таможенные сборы';
  customsDutyText = 'Таможенная пошлина';
  vatText = 'НДС';
  nonTariffRegulationMeasuresText = 'Меры нетарифного регулирования';
  totalPaymentsText = 'Общие таможенные платежи';
  saveChangesText = 'Сохранить изменения';
  savingText = 'Сохранение...';
  startCalculationText = 'Взять в работу';
  startingText = 'Взятие в работу...';
  completeCalculationText = 'Завершить расчет';
  completingText = 'Завершение...';
  calculationCompletedText = 'Расчет завершен';
  calculationInProgressText = 'Расчет в работе';
  calculationPendingText = 'Ожидает взятия в работу';
  noCustomsProductsText = 'Товары не добавлены';
  saveSuccessText = 'Изменения сохранены';
  completeSuccessText = 'Расчет завершен';
  startSuccessText = 'Расчет взят в работу';
  productNameForBrokerPlaceholderText = 'Наименование для брокера';
  productTnvedForBrokerPlaceholderText = 'Код ТНВЭД для брокера';
  totalLabelText = 'Итого:';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private cargoRequestService: CargoRequestService,
    private customsCalculationService: CustomsCalculationService,
    private languageService: LanguageService,
    private authService: AuthService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.productsForm = this.fb.group({
      products: this.fb.array([])
    });
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('CustomsBrokerViewRequest: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('CustomsBrokerViewRequest: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_CUSTOMS_BROKER') {
        console.warn('CustomsBrokerViewRequest: User is not customs broker, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      const forwarderIdParam = this.route.snapshot.queryParamMap.get('forwarderId');
      if (forwarderIdParam) {
        this.chatForwarderId = +forwarderIdParam;
      }

      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.requestId = +id;
        this.loadRequest(+id);
        this.updateTranslations();

        this.subscriptions.add(
          this.languageService.getTranslations().subscribe(() => {
            this.updateTranslations();
          })
        );
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  scrollToChat(): void {
    document.getElementById('chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private updateTranslations(): void {
    this.titleText = this.languageService.translate('customsBroker.viewRequest.title') || 'Просмотр запроса';
    this.backText = this.languageService.translate('customsBroker.viewRequest.back') || 'Назад к списку';
    this.chatButtonText = this.languageService.translate('customsBroker.viewRequest.chatButton') || 'Чат';
    this.loadingText = this.languageService.translate('customsBroker.viewRequest.loading') || 'Загрузка...';
    this.customsProductsSectionTitleText = this.languageService.translate('customsBroker.viewRequest.customsProductsSectionTitle') || 'Товары для расчета таможенных платежей';
    this.productNameText = this.languageService.translate('customsBroker.viewRequest.productName') || 'Наименование';
    this.productTnvedText = this.languageService.translate('customsBroker.viewRequest.productTnved') || 'Код ТНВЭД';
    this.productCostText = this.languageService.translate('customsBroker.viewRequest.productCost') || 'Стоимость товара';
    this.customsFeesText = this.languageService.translate('customsBroker.viewRequest.customsFees') || 'Таможенные сборы';
    this.customsDutyText = this.languageService.translate('customsBroker.viewRequest.customsDuty') || 'Таможенная пошлина';
    this.vatText = this.languageService.translate('customsBroker.viewRequest.vat') || 'НДС';
    this.nonTariffRegulationMeasuresText = this.languageService.translate('customsBroker.viewRequest.nonTariffRegulationMeasures') || 'Меры нетарифного регулирования';
    this.totalPaymentsText = this.languageService.translate('customsBroker.viewRequest.totalPayments') || 'Общие таможенные платежи';
    this.saveChangesText = this.languageService.translate('customsBroker.viewRequest.saveChanges') || 'Сохранить изменения';
    this.savingText = this.languageService.translate('customsBroker.viewRequest.saving') || 'Сохранение...';
    this.startCalculationText = this.languageService.translate('customsBroker.viewRequest.startCalculation') || 'Взять в работу';
    this.startingText = this.languageService.translate('customsBroker.viewRequest.starting') || 'Взятие в работу...';
    this.completeCalculationText = this.languageService.translate('customsBroker.viewRequest.completeCalculation') || 'Завершить расчет';
    this.completingText = this.languageService.translate('customsBroker.viewRequest.completing') || 'Завершение...';
    this.calculationCompletedText = this.languageService.translate('customsBroker.viewRequest.calculationCompleted') || 'Расчет завершен';
    this.calculationInProgressText = this.languageService.translate('customsBroker.viewRequest.calculationInProgress') || 'Расчет в работе';
    this.calculationPendingText = this.languageService.translate('customsBroker.viewRequest.calculationPending') || 'Ожидает взятия в работу';
    this.noCustomsProductsText = this.languageService.translate('customsBroker.viewRequest.noCustomsProducts') || 'Товары не добавлены';
    this.saveSuccessText = this.languageService.translate('customsBroker.viewRequest.saveSuccess') || 'Изменения сохранены';
    this.completeSuccessText = this.languageService.translate('customsBroker.viewRequest.completeSuccess') || 'Расчет завершен';
    this.startSuccessText = this.languageService.translate('customsBroker.viewRequest.startSuccess') || 'Расчет взят в работу';
    this.productNameForBrokerPlaceholderText = this.languageService.translate('customsBroker.viewRequest.productNameForBrokerPlaceholder') || this.productNameForBrokerPlaceholderText;
    this.productTnvedForBrokerPlaceholderText = this.languageService.translate('customsBroker.viewRequest.productTnvedForBrokerPlaceholder') || this.productTnvedForBrokerPlaceholderText;
    this.totalLabelText = this.languageService.translate('customsBroker.viewRequest.totalLabel') || this.totalLabelText;
    this.cdr.markForCheck();
  }

  private loadRequest(id: number): void {
    this.loadingRequest = true;
    this.subscriptions.add(
      this.cargoRequestService.getCargoRequest(id).subscribe({
        next: (request) => {
          this.request = request;
          this.loadingRequest = false;
          // Всегда синхронизируем форму с товарами для таможни (в т.ч. пустой список)
          if (request.calculateCustomsPayments && request.customsProducts != null) {
            this.populateProductsForm(request.customsProducts);
          }
          this.cdr.markForCheck();

          if (this.chatForwarderId) {
            setTimeout(() => this.scrollToChat(), 0);
          }
        },
        error: (err) => {
          console.error('CustomsBrokerViewRequest: Error loading request', err);
          this.loadingRequest = false;
          this.errorRequest = 'Ошибка при загрузке запроса';
          this.cdr.markForCheck();
        }
      })
    );
  }

  private populateProductsForm(products: CustomsProduct[]): void {
    const productsArray = this.productsForm.get('products') as FormArray;
    productsArray.clear();
    
    products.forEach(product => {
      const productGroup = this.fb.group({
        id: [product.id],
        nameOriginal: [product.nameOriginal],
        tnvedCodeOriginal: [product.tnvedCodeOriginal || ''],
        productCost: [product.productCost],
        nameForBroker: [product.nameForBroker || product.nameOriginal, Validators.required],
        tnvedCodeForBroker: [product.tnvedCodeForBroker || ''],
        customsFees: [product.customsFees || null, [Validators.min(0)]],
        customsDuty: [product.customsDuty || null, [Validators.min(0)]],
        vat: [product.vat || null, [Validators.min(0)]],
        nonTariffRegulationMeasures: [product.nonTariffRegulationMeasures || ''],
        totalCustomsPayments: [product.totalCustomsPayments || 0]
      });
      
      // Автоматически рассчитываем общие платежи при изменении
      productGroup.get('customsFees')?.valueChanges.subscribe(() => this.calculateTotalPayments(productGroup));
      productGroup.get('customsDuty')?.valueChanges.subscribe(() => this.calculateTotalPayments(productGroup));
      productGroup.get('vat')?.valueChanges.subscribe(() => this.calculateTotalPayments(productGroup));
      
      productsArray.push(productGroup);
    });
  }

  private calculateTotalPayments(productGroup: FormGroup): void {
    const fees = productGroup.get('customsFees')?.value || 0;
    const duty = productGroup.get('customsDuty')?.value || 0;
    const vat = productGroup.get('vat')?.value || 0;
    const total = fees + duty + vat;
    productGroup.get('totalCustomsPayments')?.setValue(total, { emitEvent: false });
  }

  get productsArray(): FormArray {
    return this.productsForm.get('products') as FormArray;
  }

  canSave(): boolean {
    return this.productsForm.valid && !this.submitting && !(this.request?.customsCalculationCompleted ?? false) && (this.request?.customsCalculationStarted ?? false);
  }

  canStart(): boolean {
    return !this.submitting && !(this.request?.customsCalculationStarted ?? false) && !(this.request?.customsCalculationCompleted ?? false);
  }

  canComplete(): boolean {
    if (!this.request || (this.request.customsCalculationCompleted ?? false)) {
      return false;
    }
    
    // Проверяем, что все товары заполнены
    const productsArray = this.productsArray;
    for (let i = 0; i < productsArray.length; i++) {
      const productGroup = productsArray.at(i) as FormGroup;
      const nameForBroker = productGroup.get('nameForBroker')?.value;
      const customsFees = productGroup.get('customsFees')?.value;
      const customsDuty = productGroup.get('customsDuty')?.value;
      const vat = productGroup.get('vat')?.value;
      
      if (!nameForBroker || customsFees === null || customsDuty === null || vat === null) {
        return false;
      }
    }
    
    return true;
  }

  saveChanges(): void {
    if (!this.requestId || !this.canSave()) {
      return;
    }

    this.submitting = true;
    this.error = null;
    this.success = false;

    const productsArray = this.productsArray;
    const updateObservables = [];

    for (let i = 0; i < productsArray.length; i++) {
      const productGroup = productsArray.at(i) as FormGroup;
      const productId = productGroup.get('id')?.value;
      
      const updateRequest: UpdateCustomsProductRequest = {
        nameForBroker: productGroup.get('nameForBroker')?.value,
        tnvedCodeForBroker: productGroup.get('tnvedCodeForBroker')?.value || undefined,
        customsFees: productGroup.get('customsFees')?.value || undefined,
        customsDuty: productGroup.get('customsDuty')?.value || undefined,
        vat: productGroup.get('vat')?.value || undefined,
        nonTariffRegulationMeasures: productGroup.get('nonTariffRegulationMeasures')?.value || undefined
      };

      updateObservables.push(
        this.customsCalculationService.updateProduct(this.requestId, productId, updateRequest)
      );
    }

    // Если нет товаров для обновления, просто выходим
    if (updateObservables.length === 0) {
      this.submitting = false;
      return;
    }

    this.subscriptions.add(
      forkJoin(updateObservables).subscribe({
        next: () => {
          this.success = true;
          this.submitting = false;
          // Перезагружаем запрос для обновления данных
          this.loadRequest(this.requestId!);
          setTimeout(() => {
            this.success = false;
            this.cdr.markForCheck();
          }, 3000);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error saving products:', err);
          this.error = 'Ошибка при сохранении изменений: ' + (err.error?.message || 'Неизвестная ошибка');
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  async startCalculation(): Promise<void> {
    if (!this.requestId || !this.canStart()) {
      return;
    }

    if (!await this.confirmDialog.confirm('Вы уверены, что хотите взять этот расчет в работу? После этого грузовладелец не сможет редактировать товары.', { danger: false, confirmText: 'Взять в работу' })) {
      return;
    }

    this.submitting = true;
    this.error = null;
    this.success = false;

    this.subscriptions.add(
      this.customsCalculationService.startCalculation(this.requestId).subscribe({
        next: () => {
          this.success = true;
          this.submitting = false;
          // Перезагружаем запрос для обновления статуса
          this.loadRequest(this.requestId!);
          setTimeout(() => {
            this.success = false;
            this.cdr.markForCheck();
          }, 3000);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error starting calculation:', err);
          this.error = 'Ошибка при взятии в работу: ' + (err.error?.message || 'Неизвестная ошибка');
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  async completeCalculation(): Promise<void> {
    if (!this.requestId || !this.canComplete()) {
      return;
    }

    if (!await this.confirmDialog.confirm('Вы уверены, что хотите завершить расчет таможенных платежей? После завершения редактирование будет недоступно.', { danger: false, confirmText: 'Завершить' })) {
      return;
    }

    this.submitting = true;
    this.error = null;
    this.success = false;

    this.subscriptions.add(
      this.customsCalculationService.completeCalculation(this.requestId).subscribe({
        next: () => {
          this.success = true;
          this.submitting = false;
          // Перезагружаем запрос для обновления статуса
          this.loadRequest(this.requestId!);
          setTimeout(() => {
            this.success = false;
            this.cdr.markForCheck();
          }, 3000);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error completing calculation:', err);
          this.error = 'Ошибка при завершении расчета: ' + (err.error?.message || 'Неизвестная ошибка');
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getTotalAllPayments(): number {
    const productsArray = this.productsArray;
    let total = 0;
    for (let i = 0; i < productsArray.length; i++) {
      const productGroup = productsArray.at(i) as FormGroup;
      total += productGroup.get('totalCustomsPayments')?.value || 0;
    }
    return total;
  }
}
